// Language selector — global header, every role (clients included).
//
// v1 scope: this preference drives the LANGUAGE THE ASSISTANT ANSWERS IN
// (Ask Euphoric now, briefings next). Full UI-chrome translation and RTL
// layout are separate, later projects — the dropdown is deliberately the
// same control that will drive those when they land.
//
// The list comes from the backend registry (services/languages.py) via
// /intelligence/ask/capabilities' sibling data; we keep a static mirror
// here so the dropdown renders instantly and for client-role users who
// don't call that endpoint. Keep the two lists aligned.

import React, { useState, useEffect, useRef } from 'react';
import { Globe, Check } from 'lucide-react';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';

export const LANGUAGES = [
  { code: 'en', native: 'English' },
  { code: 'ar', native: 'العربية' },
  { code: 'hi', native: 'हिन्दी' },
  { code: 'ur', native: 'اردو' },
  { code: 'te', native: 'తెలుగు' },
  { code: 'ta', native: 'தமிழ்' },
  { code: 'bn', native: 'বাংলা' },
  { code: 'pa', native: 'ਪੰਜਾਬੀ' },
  { code: 'gu', native: 'ગુજરાતી' },
  { code: 'mr', native: 'मराठी' },
  { code: 'ml', native: 'മലയാളം' },
  { code: 'kn', native: 'ಕನ್ನಡ' },
  { code: 'ru', native: 'Русский' },
  { code: 'fr', native: 'Français' },
  { code: 'es', native: 'Español' },
  { code: 'pt', native: 'Português' },
  { code: 'de', native: 'Deutsch' },
  { code: 'fa', native: 'فارسی' },
  { code: 'tl', native: 'Filipino' },
  { code: 'zh', native: '中文' },
];

const LS_KEY = 'preferred_language';

export function getPreferredLanguage() {
  return localStorage.getItem(LS_KEY) || 'en';
}

export default function LanguageSelector() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(getPreferredLanguage());
  const ref = useRef(null);

  useEffect(() => {
    // Sync from the server once — the server value wins over localStorage
    // so the preference follows the user across devices.
    authAPI.getMe().then((res) => {
      const server = res.data?.preferred_language;
      if (server && server !== getPreferredLanguage()) {
        localStorage.setItem(LS_KEY, server);
        setCurrent(server);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const select = async (code) => {
    setOpen(false);
    const prev = current;
    setCurrent(code);
    localStorage.setItem(LS_KEY, code);
    try {
      await authAPI.setLanguage(code);
      const lang = LANGUAGES.find((l) => l.code === code);
      toast.success(`Euphoric will answer you in ${lang?.native || code}`);
    } catch {
      setCurrent(prev);
      localStorage.setItem(LS_KEY, prev);
      toast.error('Could not save language preference');
    }
  };

  const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2.5 py-1.5 rounded-lg transition-colors"
        title="Answer language"
      >
        <Globe size={16} />
        <span className="hidden sm:inline text-xs font-medium">{active.native}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 max-h-80 overflow-y-auto bg-white rounded-xl border border-slate-200 shadow-lg py-1 z-50">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => select(l.code)}
              className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left hover:bg-slate-50 transition-colors ${
                l.code === current ? 'text-teal-700 font-medium' : 'text-slate-700'
              }`}
            >
              {l.native}
              {l.code === current && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
